import { useCallback, useEffect, useRef, useState } from "react";
import type { TeamPlayer, PlayerPosition } from "../../types";
import { formatPlayerName } from "../../lib/nameFormat";

export type LineupZone = "starters" | "bench" | "excluded";

export const MAX_LINEUP_STARTERS = 11;
export const MAX_LINEUP_SUBSTITUTES = 12;
/**
 * Mirrors the backend's MAX_LINEUP_SIZE (matches_crud.py / matches_new.py).
 * Now exactly the sum of the two caps above, so it can no longer be reached
 * by a combination the per-zone caps would allow.
 */
export const MAX_MATCHDAY_SQUAD = MAX_LINEUP_STARTERS + MAX_LINEUP_SUBSTITUTES;

/** Per-zone cap messages, resolved by the caller so this stays i18n-free. */
export interface LineupCapMessages {
  starters: string;
  substitutes: string;
}

export interface LineupPlayerSelection {
  player_id: string;
  player_name: string;
  position: PlayerPosition;
  /** EPIC-95 T5: nullable — a squad player called up before their team-link
   *  jersey number was completed keeps `null` here until the admin fills it
   *  in via the board's own inline edit. `findMissingJerseyNumberPlayers`
   *  is the save-time gate; the backend's own lineup schema still requires
   *  a real int and would reject a `null` sent through. */
  jersey_number: number | null;
  is_starter: boolean;
  is_captain?: boolean;
}

export interface LineupChip {
  player: TeamPlayer;
  selectable: boolean;
}

// EPIC-95 T5: a missing jersey number no longer makes a candidate
// unselectable — it can still be called up to starters/bench (the board's
// own inline edit is how the admin fills it in once placed); only a missing
// position still blocks the move entirely (never a reachable state through
// any live path today, kept as a defensive no-op).
const isSelectable = (player: TeamPlayer): boolean => Boolean(player.position);

/**
 * Pure: which zone a candidate currently occupies.
 *
 * `lineup` IS the matchday squad — a candidate with an entry is a starter
 * or an explicit substitute, and a candidate with no entry is out of the
 * squad. There is no third piece of state.
 *
 * This inverts the previous default (client request 2026-08-18: "all
 * players from the teams needs to be in the out of convocatory column by
 * default"). It also deletes the `excludedIds` set that used to carry the
 * distinction: that set was client-only and never sent to the server, so
 * "excluded" and "untouched" were indistinguishable the moment the modal
 * closed, and the previous default silently called up the entire roster as
 * substitutes. Now the saved payload is the only source of truth and an
 * edit round-trip is exact rather than inferred.
 */
export function resolveCurrentZone(
  lineup: LineupPlayerSelection[],
  playerId: string,
): LineupZone {
  const selection = lineup.find((p) => p.player_id === playerId);
  if (!selection) return "excluded";
  return selection.is_starter ? "starters" : "bench";
}

/**
 * Pure: derive starters/bench/excluded chip lists from the roster + the
 * current lineup selections. Every active roster candidate is a chip in
 * exactly one of the three zones, and every candidate the user has not
 * called up lands in "excluded".
 */
export function computeLineupChips(
  candidates: TeamPlayer[],
  lineup: LineupPlayerSelection[],
): { starters: LineupChip[]; bench: LineupChip[]; excluded: LineupChip[] } {
  const starters: LineupChip[] = [];
  const bench: LineupChip[] = [];
  const excluded: LineupChip[] = [];
  for (const player of candidates) {
    const chip: LineupChip = { player, selectable: isSelectable(player) };
    const zone = resolveCurrentZone(lineup, player.player_id);
    if (zone === "starters") starters.push(chip);
    else if (zone === "excluded") excluded.push(chip);
    else bench.push(chip);
  }
  return { starters, bench, excluded };
}

/**
 * Save-time entry point — call this, not `lineup` directly, from every save
 * path (create payload, edit lineup PATCH, cockpit pre-match panel).
 *
 * It is now an identity function, and that is the point: `lineup` already
 * holds exactly the called-up squad, so there is nothing to materialize.
 * The previous implementation appended every untouched roster candidate as
 * an implicit `is_starter: false` substitute, which is precisely the
 * behaviour the client asked to remove — a match saved after picking only a
 * starting XI came back with the whole roster on the bench.
 *
 * Kept as a named function rather than inlined so the save paths keep one
 * obvious seam to hook if a future rule (auto-captain, ordering) needs to
 * run at save time, and so this doc stays attached to the decision.
 */
export function finalizeLineupForSave(
  lineup: LineupPlayerSelection[],
): LineupPlayerSelection[] {
  return lineup;
}

/**
 * Which cap, if any, a finished lineup violates — `null` when it is fine.
 *
 * The board refuses a move past either cap, so this only ever fires on a
 * lineup that did not come from the board: a match saved before the caps
 * existed and reopened for edit. Save is blocked rather than the lineup
 * being silently truncated, so the operator chooses who to drop.
 *
 * Exported (rather than living inside the form component) so the save-path
 * guard and its tests exercise the same code — the previous form validator
 * was re-implemented inside its own test file and could have drifted from
 * the component without a single test failing.
 */
export function lineupCapViolation(
  lineup: LineupPlayerSelection[],
): "starters" | "substitutes" | null {
  const starters = lineup.filter((p) => p.is_starter).length;
  if (starters > MAX_LINEUP_STARTERS) return "starters";
  if (lineup.length - starters > MAX_LINEUP_SUBSTITUTES) return "substitutes";
  return null;
}

/**
 * Save-time gate for EPIC-95 T5: which called-up players (starters or bench
 * — `lineup` never holds an excluded player, see `resolveCurrentZone`'s own
 * doc comment) still have no jersey number. Empty when the lineup is clean.
 *
 * Deliberately NOT enforced at assign time (`resolveLineupAssignment` lets a
 * null-jersey player be called up) — the lead's design: the admin sees the
 * gap on the placed chip and fills it in via the board's own inline editor,
 * and save is what actually blocks, naming every still-missing player at
 * once rather than one at a time on the first attempted move.
 */
export function findMissingJerseyNumberPlayers(
  lineup: LineupPlayerSelection[],
): LineupPlayerSelection[] {
  return lineup.filter(
    (p) => p.jersey_number === null || p.jersey_number === undefined,
  );
}

export interface LineupAssignmentResult {
  lineup: LineupPlayerSelection[];
  error: string | null;
}

/**
 * Pure state-machine step: resolve what the lineup array should look like
 * after moving `player` into `targetZone`.
 *
 * Moving to "excluded" removes the player's entry from `lineup` entirely —
 * that absence IS the out-of-convocatoria state, there is no side set to
 * maintain. Moving to "starters" or "bench" adds or updates the entry,
 * subject to that zone's cap.
 *
 * Both caps are hard (client request 2026-08-18: "enforce that there is a
 * max starters of 11 and a max of 12 substitutes"). A blocked move is a
 * no-op that returns the SAME `lineup` reference plus the zone's own
 * message, so the chip visibly stays put and the player remains in the
 * out-of-convocatoria column rather than being silently dropped somewhere
 * else.
 *
 * The caps are enforced against the count of OTHER players in the target
 * zone, so a player already in that zone moving within it (which
 * `currentZone === targetZone` short-circuits anyway) can never be blocked
 * by their own presence, and a bench player promoted to a full starting XI
 * is refused without first losing their bench slot.
 */
export function resolveLineupAssignment(
  lineup: LineupPlayerSelection[],
  player: TeamPlayer,
  targetZone: LineupZone,
  capMessages: LineupCapMessages,
): LineupAssignmentResult {
  // No position: unchanged silent no-op (a candidate is never missing a
  // position through any live path today).
  if (!player.position) {
    return { lineup, error: null };
  }
  // EPIC-95 T5 (lead's revised design, superseding this function's earlier
  // hard block): a missing jersey number (`player.jersey_number` can now be
  // `null` — a roster-import row the admin never typed one for) no longer
  // refuses the move. The player CAN be called up to starters/bench with a
  // `null` entry; `findMissingJerseyNumberPlayers` (below) is the save-time
  // gate — each caller (MatchesManager.tsx, PreMatchLineupPanel.tsx) names
  // the still-missing players in its own save-validation message, and the
  // board's own inline jersey/name editor (already reachable on any placed
  // chip) is how the admin resolves it before saving.

  const currentZone = resolveCurrentZone(lineup, player.player_id);
  if (currentZone === targetZone) {
    return { lineup, error: null };
  }

  if (targetZone === "excluded") {
    const nextLineup = lineup.filter((p) => p.player_id !== player.player_id);
    return {
      lineup: nextLineup.length === lineup.length ? lineup : nextLineup,
      error: null,
    };
  }

  const isStarter = targetZone === "starters";
  const others = lineup.filter((p) => p.player_id !== player.player_id);
  const zoneCount = others.filter((p) => p.is_starter === isStarter).length;
  const zoneMax = isStarter ? MAX_LINEUP_STARTERS : MAX_LINEUP_SUBSTITUTES;
  if (zoneCount >= zoneMax) {
    return {
      lineup,
      error: isStarter ? capMessages.starters : capMessages.substitutes,
    };
  }

  const existingIndex = lineup.findIndex(
    (p) => p.player_id === player.player_id,
  );
  if (existingIndex >= 0) {
    const updated = [...lineup];
    updated[existingIndex] = {
      ...updated[existingIndex],
      is_starter: isStarter,
    };
    return { lineup: updated, error: null };
  }

  const newEntry: LineupPlayerSelection = {
    player_id: player.player_id,
    player_name: formatPlayerName(player.player_name || player.player_id),
    position: player.position,
    jersey_number: player.jersey_number,
    is_starter: isStarter,
  };
  return { lineup: [...lineup, newEntry], error: null };
}

/**
 * Tap/keyboard toggle target — a single-button cycle per current zone.
 *
 * excluded -> bench -> starters -> bench. Tapping never sends a player back
 * to the out-of-convocatoria column: that is a deliberate removal, and it
 * has its own explicit affordance on the chip (`PlayerChip`'s
 * remove-from-squad button). Making a stray tap on a starter drop them out
 * of the squad entirely would be a destructive accident, where the current
 * cycle's worst case is demoting them to the bench.
 */
export const toggleTargetZone = (currentZone: LineupZone): LineupZone => {
  switch (currentZone) {
    case "starters":
      return "bench";
    case "excluded":
      return "bench";
    case "bench":
    default:
      return "starters";
  }
};

const resolveDropZone = (
  clientX: number,
  clientY: number,
): LineupZone | null => {
  const el = document.elementFromPoint(clientX, clientY);
  if (!(el instanceof HTMLElement)) return null;
  const zoneEl = el.closest("[data-lineup-dropzone]");
  const zone = zoneEl?.getAttribute("data-lineup-dropzone");
  return zone === "starters" || zone === "bench" || zone === "excluded"
    ? zone
    : null;
};

interface UseLineupDndArgs {
  candidates: TeamPlayer[];
  lineup: LineupPlayerSelection[];
  disabled: boolean;
  onChange: (lineup: LineupPlayerSelection[]) => void;
  capMessages: LineupCapMessages;
  /**
   * PMA-COCK-171: the live in-cockpit bench panel keeps the starting XI
   * locked once the match has kicked off (no move to/from "starters" in
   * either direction) while bench <-> not-in-squad stays fully editable.
   * `false`/omitted preserves the pre-match board's unrestricted behavior.
   */
  benchOnly?: boolean;
  /**
   * Individual players who cannot be moved at all regardless of zone — the
   * live bench panel locks a substitute who has already entered the field
   * (an active Substitution event exists for them) so "quitar de la
   * convocatoria" can't pull a playing player off the matchday squad.
   */
  lockedPlayerIds?: Set<string>;
}

export interface ChipDragProps {
  draggable: boolean;
  onDragStart: (event: React.DragEvent<HTMLButtonElement>) => void;
  onDragEnd: () => void;
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onClick: () => void;
}

export interface ZoneDropProps {
  "data-lineup-dropzone": LineupZone;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDragLeave: () => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}

export interface UseLineupDndResult {
  starters: LineupChip[];
  bench: LineupChip[];
  excluded: LineupChip[];
  draggingPlayerId: string | null;
  dragOverZone: LineupZone | null;
  capError: string | null;
  assign: (player: TeamPlayer, targetZone: LineupZone) => void;
  chipProps: (player: TeamPlayer, zone: LineupZone) => ChipDragProps;
  zoneProps: (zone: LineupZone) => ZoneDropProps;
}

/**
 * Drag/tap state machine for the lineup board. Native HTML5 drag events
 * drive desktop mouse drags; a pointer-event fallback (same pattern as
 * TacticalField.tsx's bench-drag precedent) handles touch, since touch
 * browsers don't reliably fire native drag events. Tap-to-toggle and
 * keyboard (Enter/Space) parity fall out for free because chips are plain
 * `<button>` elements wired to `onClick`. A cancelled or invalid drag never
 * calls `onChange`/`onExcludedChange` — state only mutates inside a
 * successful drop/tap.
 */
export function useLineupDnd({
  candidates,
  lineup,
  disabled,
  onChange,
  capMessages,
  benchOnly = false,
  lockedPlayerIds,
}: UseLineupDndArgs): UseLineupDndResult {
  const [draggingPlayerId, setDraggingPlayerId] = useState<string | null>(null);
  const [dragOverZone, setDragOverZone] = useState<LineupZone | null>(null);
  const [capError, setCapError] = useState<string | null>(null);
  const suppressClickRef = useRef<string | null>(null);
  const pointerDragRef = useRef<{
    player: TeamPlayer;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);

  const { starters, bench, excluded } = computeLineupChips(candidates, lineup);

  /** True when `player` may not move into/out of `zone` at all — the
   * authoritative gate behind both the drag/drop and tap-to-toggle entry
   * points (chip `draggable`/`disabled` attributes are a UX nicety on top of
   * this, not a substitute for it: native drag/pointer events can still
   * reach `assign` directly). */
  const isMoveLocked = useCallback(
    (player: TeamPlayer, zone: LineupZone): boolean =>
      Boolean(lockedPlayerIds?.has(player.player_id)) ||
      (benchOnly && zone === "starters"),
    [benchOnly, lockedPlayerIds],
  );

  const assign = useCallback(
    (player: TeamPlayer, targetZone: LineupZone) => {
      if (disabled) return;
      const currentZone = resolveCurrentZone(lineup, player.player_id);
      if (
        isMoveLocked(player, currentZone) ||
        isMoveLocked(player, targetZone)
      ) {
        return;
      }
      const result = resolveLineupAssignment(
        lineup,
        player,
        targetZone,
        capMessages,
      );
      if (result.error) {
        setCapError(result.error);
        return;
      }
      setCapError(null);
      if (result.lineup !== lineup) {
        onChange(result.lineup);
      }
    },
    [disabled, lineup, onChange, capMessages, isMoveLocked],
  );

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const drag = pointerDragRef.current;
    if (!drag) return;
    const distance = Math.hypot(
      event.clientX - drag.startX,
      event.clientY - drag.startY,
    );
    if (!drag.moved && distance < 6) return;
    if (!drag.moved) {
      drag.moved = true;
      suppressClickRef.current = drag.player.player_id;
      setDraggingPlayerId(drag.player.player_id);
    }
    setDragOverZone(resolveDropZone(event.clientX, event.clientY));
  }, []);

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      const drag = pointerDragRef.current;
      pointerDragRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      setDraggingPlayerId(null);
      setDragOverZone(null);
      if (!drag || !drag.moved) return;
      const zone = resolveDropZone(event.clientX, event.clientY);
      if (zone) {
        assign(drag.player, zone);
      }
      // No zone hit (cancelled/invalid drop) — deliberately a no-op.
    },
    [assign, handlePointerMove],
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerMove, handlePointerUp]);

  const chipProps = useCallback(
    (player: TeamPlayer, zone: LineupZone): ChipDragProps => {
      const locked = isMoveLocked(player, zone);
      return {
        draggable: !disabled && !locked,
        onDragStart: (event) => {
          if (disabled || locked) return;
          event.dataTransfer.setData("text/plain", player.player_id);
          event.dataTransfer.effectAllowed = "move";
          setDraggingPlayerId(player.player_id);
        },
        onDragEnd: () => {
          setDraggingPlayerId(null);
          setDragOverZone(null);
        },
        onPointerDown: (event) => {
          if (disabled || locked || event.pointerType !== "touch") return;
          pointerDragRef.current = {
            player,
            startX: event.clientX,
            startY: event.clientY,
            moved: false,
          };
          window.addEventListener("pointermove", handlePointerMove);
          window.addEventListener("pointerup", handlePointerUp);
        },
        onClick: () => {
          if (disabled || locked) return;
          if (suppressClickRef.current === player.player_id) {
            suppressClickRef.current = null;
            return;
          }
          assign(player, toggleTargetZone(zone));
        },
      };
    },
    [disabled, handlePointerMove, handlePointerUp, assign, isMoveLocked],
  );

  const zoneProps = useCallback(
    (zone: LineupZone): ZoneDropProps => ({
      "data-lineup-dropzone": zone,
      onDragOver: (event) => {
        if (disabled || (benchOnly && zone === "starters")) return;
        event.preventDefault();
        setDragOverZone(zone);
      },
      onDragLeave: () => {
        setDragOverZone((prev) => (prev === zone ? null : prev));
      },
      onDrop: (event) => {
        event.preventDefault();
        setDraggingPlayerId(null);
        setDragOverZone(null);
        if (disabled || (benchOnly && zone === "starters")) return;
        const playerId = event.dataTransfer.getData("text/plain");
        const player = candidates.find((c) => c.player_id === playerId);
        if (player) {
          assign(player, zone);
        }
      },
    }),
    [disabled, candidates, assign, benchOnly],
  );

  return {
    starters,
    bench,
    excluded,
    draggingPlayerId,
    dragOverZone,
    capError,
    assign,
    chipProps,
    zoneProps,
  };
}
